import type { OtimAPIClient } from "@api/api-client";
import type { InstructionActionValue } from "@schemas/instruction";
import type { InstructionAction as InstructionActionType } from "@schemas/instruction";
import type { Address, InstructionId } from "@schemas/misc";
import type { WalletClient } from "viem";

import { decodeAbiParameters, pad, recoverTypedDataAddress, toHex } from "viem";

import {
  type Instruction,
  type InstructionActivateRequest,
  type InstructionActivateResponse,
  type InstructionBuildRequest,
  type InstructionDetailsRequest,
  type InstructionDetailsResponse,
  type InstructionGetAllRequest,
  type InstructionGetAllResponse,
} from "@api/schemas/instruction";
import { getDelegateAddress } from "@client/config";
import {
  createInstructionActivationTypes,
  createOtimDelegateDomain,
} from "@config/eip-712";
import { InstructionAction } from "@schemas/instruction";
import { decodeBuildInstructionActionArguments } from "@utils/helpers/decode-abi";
import { generateInstructionSalt } from "@utils/helpers/instruction";
import {
  createEIP2098Signature,
  parseSignatureToVRS,
} from "@utils/helpers/misc";

export interface BaseInstructionOptions {
  address?: Address;
  chainId?: number;
  salt?: number;
  maxExecutions?: number;
}

export interface BaseFeeOptions {
  token?: Address;
  maxBaseFeePerGas?: bigint | string;
  maxPriorityFeePerGas?: bigint | string;
  executionFee?: number;
}

export interface BaseScheduleOptions {
  startAt?: number;
  startBy?: number;
  interval?: number;
  timeout?: number;
}

export interface InstructionBuildResult<T = unknown> {
  instruction: T;
  actionType: InstructionActionType;
  activate: (options?: {
    nickname?: string;
  }) => Promise<InstructionActivateResponse>;
}

// transfer options
export interface TransferOptions extends BaseInstructionOptions {
  target: Address;
  value: bigint;
  token?: Address;
  gasLimit?: bigint;
  schedule?: BaseScheduleOptions;
  fee?: BaseFeeOptions;
}

// sweep options
export interface SweepOptions extends BaseInstructionOptions {
  depositor: Address;
  recipient: Address;
  threshold?: bigint;
  token?: Address;
  fee?: BaseFeeOptions;
}

export interface SweepSkipCCTPDepositAccountOptions
  extends BaseInstructionOptions {
  depositor: Address;
  destinationDomain: number;
  destinationMintRecipient: Address;
  threshold?: bigint;
  fee?: BaseFeeOptions;
}

// swap options
export interface SwapOptions extends BaseInstructionOptions {
  tokenIn: Address;
  tokenOut: Address;
  amountIn: bigint;
  floorAmountOut: bigint;
  recipient?: Address;
  feeTier?: number;
  meanPriceLookBack?: number;
  maxPriceDeviationBps?: number;
  schedule?: BaseScheduleOptions;
  fee?: BaseFeeOptions;
}

// refuel options
export interface RefuelOptions extends BaseInstructionOptions {
  target?: Address;
  threshold: bigint;
  endBalance: bigint;
  token?: Address;
  gasLimit?: bigint;
  fee?: BaseFeeOptions;
}

export interface BuildDeactivateInstructionOptions
  extends BaseInstructionOptions {
  instructionId: InstructionId;
  fee?: BaseFeeOptions;
}

// main instruction client options
export interface GetAllInstructionsOptions {
  address?: Address;
  chainId?: number;
}

export interface GetInstructionDetailsOptions {
  instructionId: Address;
}

export interface ActivateInstructionOptions {
  instruction: Instruction;
  actionType: InstructionActionValue;
  nickname?: string;
}

// creates base fee object with default values and converts to hex
export const createFeeArguments = (fee: BaseFeeOptions = {}) => {
  const baseFee = {
    token: fee.token || "0x0000000000000000000000000000000000000000",
    maxBaseFeePerGas: toHex(fee.maxBaseFeePerGas || 0n),
    maxPriorityFeePerGas: toHex(fee.maxPriorityFeePerGas || 0n),
    executionFee: fee.executionFee || 0,
  };

  return baseFee;
};

// resolves schedule settings, excluding it for sweep actions
export const resolveSchedule = (
  actionType: InstructionActionType,
  schedule: BaseScheduleOptions = {},
) => {
  if (
    actionType === InstructionAction.SweepDepositAccount ||
    actionType === InstructionAction.SweepDepositAccountERC20 ||
    actionType === InstructionAction.SweepSkipCCTPDepositAccount
  ) {
    return {};
  }

  return {
    schedule: {
      startAt: schedule.startAt || 0,
      startBy: schedule.startBy || 0,
      interval: schedule.interval || 60,
      timeout: schedule.timeout || 0,
    },
  };
};

// validates wallet client connection
export function validateWalletClient(
  walletClient: WalletClient,
  options: BaseInstructionOptions,
) {
  if (!options.address && !walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!options.chainId && !walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  return {
    address: options.address || walletClient.account!.address,
    chainId: options.chainId || walletClient.chain!.id,
  };
}

export function createInstructionSalt(salt?: number): number {
  return salt || generateInstructionSalt();
}

export async function buildInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  actionType: InstructionActionType,
  actionArguments: Record<string, unknown>,
  options: BaseInstructionOptions,
): Promise<InstructionBuildResult> {
  const wallet = validateWalletClient(walletClient, options);
  const instructionSalt = createInstructionSalt(options.salt);

  const buildRequest: InstructionBuildRequest = {
    address: wallet.address,
    chainId: wallet.chainId,
    salt: instructionSalt,
    maxExecutions: options.maxExecutions ?? 0,
    actionArguments: {
      [actionType]: actionArguments,
    },
  };

  const buildResponse = await client.instruction.build(buildRequest);

  // create activate callback that captures the instruction and actionType
  const activateCallback = async (options?: { nickname?: string }) => {
    const activateOptions: ActivateInstructionOptions = {
      instruction: buildResponse.data,
      actionType,
    };

    if (options?.nickname !== undefined) {
      activateOptions.nickname = options.nickname;
    }

    return activate(client, walletClient, activateOptions);
  };

  return {
    instruction: buildResponse.data,
    actionType,
    activate: activateCallback,
  };
}

function getTransferActionType(token?: Address) {
  return token ? InstructionAction.TransferERC20 : InstructionAction.Transfer;
}

function buildTransferActionArguments(options: TransferOptions) {
  const { target, value, token, gasLimit = 0n } = options;
  const actionType = getTransferActionType(token);

  const baseArgs = {
    target,
    value: toHex(value),
    ...(token && { token }),
    ...resolveSchedule(actionType, options.schedule),
    fee: createFeeArguments(options.fee),
  };

  return actionType === InstructionAction.Transfer
    ? { ...baseArgs, gasLimit: toHex(gasLimit) }
    : baseArgs;
}

export async function buildTransferInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  options: TransferOptions,
): Promise<InstructionBuildResult> {
  const actionType = getTransferActionType(options.token);
  const actionArguments = buildTransferActionArguments(options);

  return buildInstruction(
    client,
    walletClient,
    actionType,
    actionArguments,
    options,
  );
}

function getSweepActionType(token?: Address) {
  return token
    ? InstructionAction.SweepDepositAccountERC20
    : InstructionAction.SweepDepositAccount;
}

function buildSweepActionArguments(options: SweepOptions) {
  const { depositor, recipient, threshold, token } = options;

  const baseArgs = {
    depositor,
    recipient,
    threshold: toHex(threshold || 0n),
    fee: createFeeArguments(options.fee),
  };

  return token ? { ...baseArgs, token } : baseArgs;
}

export async function buildSweepInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  options: SweepOptions,
): Promise<InstructionBuildResult> {
  const actionType = getSweepActionType(options.token);
  const actionArguments = buildSweepActionArguments(options);

  return buildInstruction(
    client,
    walletClient,
    actionType,
    actionArguments,
    options,
  );
}

export async function buildSweepSkipCCTPDepositAccountInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  options: SweepSkipCCTPDepositAccountOptions,
): Promise<InstructionBuildResult> {
  const actionType = InstructionAction.SweepSkipCCTPDepositAccount;
  const actionArguments = {
    depositor: options.depositor,
    destinationDomain: options.destinationDomain,
    destinationMintRecipient: options.destinationMintRecipient,
    threshold: toHex(options.threshold || 0n),
    fee: createFeeArguments(options.fee),
  };

  return buildInstruction(
    client,
    walletClient,
    actionType,
    actionArguments,
    options,
  );
}

function buildSwapActionArguments(options: SwapOptions, recipient: Address) {
  const {
    tokenIn,
    tokenOut,
    amountIn,
    floorAmountOut,
    feeTier = 500,
    meanPriceLookBack,
    maxPriceDeviationBps,
    schedule,
    fee,
  } = options;

  const actionType = InstructionAction.UniswapV3ExactInput;

  return {
    recipient,
    tokenIn,
    tokenOut,
    amountIn: toHex(amountIn),
    floorAmountOut: toHex(floorAmountOut),
    feeTier,
    meanPriceLookBack: meanPriceLookBack ?? 1800,
    maxPriceDeviationBps: maxPriceDeviationBps ?? 5000,
    ...resolveSchedule(actionType, schedule),
    fee: createFeeArguments(fee),
  };
}

export async function buildSwapInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  options: SwapOptions,
): Promise<InstructionBuildResult> {
  const wallet = validateWalletClient(walletClient, options);
  const resolvedRecipient = options.recipient || wallet.address;
  const actionArguments = buildSwapActionArguments(options, resolvedRecipient);

  return buildInstruction(
    client,
    walletClient,
    InstructionAction.UniswapV3ExactInput,
    actionArguments,
    options,
  );
}

function getRefuelActionType(token?: Address) {
  return token ? InstructionAction.RefuelERC20 : InstructionAction.Refuel;
}

function buildRefuelActionArguments(options: RefuelOptions, target: Address) {
  const { threshold, endBalance, token, gasLimit, fee } = options;
  const actionType = getRefuelActionType(token);

  const baseArgs = {
    target,
    threshold: toHex(threshold),
    endBalance: toHex(endBalance),
    ...(token && { token }),
    fee: createFeeArguments(fee),
  };

  return actionType === InstructionAction.Refuel
    ? { ...baseArgs, gasLimit: toHex(gasLimit || 0n) }
    : baseArgs;
}

export async function buildRefuelInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  options: RefuelOptions,
): Promise<InstructionBuildResult> {
  const wallet = validateWalletClient(walletClient, options);
  const resolvedTarget = options.target || wallet.address;
  const actionType = getRefuelActionType(options.token);
  const actionArguments = buildRefuelActionArguments(options, resolvedTarget);

  return buildInstruction(
    client,
    walletClient,
    actionType,
    actionArguments,
    options,
  );
}

// build deactivate instruction
function buildDeactivateActionArguments(
  options: BuildDeactivateInstructionOptions,
) {
  return {
    instructionId: options.instructionId,
    fee: createFeeArguments(options.fee),
  };
}

export async function buildDeactivateInstruction(
  client: OtimAPIClient,
  walletClient: WalletClient,
  options: BuildDeactivateInstructionOptions,
): Promise<InstructionBuildResult> {
  const actionType = InstructionAction.DeactivateInstruction;
  const actionArguments = buildDeactivateActionArguments(options);

  return buildInstruction(
    client,
    walletClient,
    actionType,
    actionArguments,
    options,
  );
}

export async function getAll(
  client: OtimAPIClient,
  walletClient: WalletClient,
  { address, chainId }: GetAllInstructionsOptions = {},
): Promise<InstructionGetAllResponse> {
  if (!address && !walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!chainId && !walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  const targetAddress = address || walletClient.account!.address;
  const targetChainId = chainId || walletClient.chain!.id;

  const requestParams: InstructionGetAllRequest = {
    address: targetAddress,
    chainId: targetChainId,
  };

  const response = await client.instruction.getAll(requestParams);
  return response.data;
}

export async function details(
  client: OtimAPIClient,
  walletClient: WalletClient,
  { instructionId }: GetInstructionDetailsOptions,
): Promise<InstructionDetailsResponse> {
  if (!walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  const targetAddress = walletClient.account.address;
  const targetChainId = walletClient.chain.id;

  const requestBody: InstructionDetailsRequest = {
    address: targetAddress,
    chainId: targetChainId,
    instructionId,
  };

  const response = await client.instruction.getDetails(requestBody);
  return response.data;
}

export async function activate(
  client: OtimAPIClient,
  walletClient: WalletClient,
  { instruction, actionType, nickname }: ActivateInstructionOptions,
): Promise<InstructionActivateResponse> {
  if (!walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  const targetAddress = walletClient.account.address;
  const targetChainId = walletClient.chain.id;

  const delegateAddressResponse = await getDelegateAddress(client, {
    chainId: targetChainId,
  });

  const domain = createOtimDelegateDomain(
    delegateAddressResponse.otimDelegateAddress as Address,
    targetChainId,
  );
  const types = createInstructionActivationTypes(actionType);

  const decodedArguments = decodeBuildInstructionActionArguments(
    actionType,
    instruction.arguments,
  );

  // decode parameters for eip-712 signing
  const [decodedSalt] = decodeAbiParameters(
    [{ type: "uint256" }],
    pad(instruction.salt),
  );

  const [decodedMaxExecutions] = decodeAbiParameters(
    [{ type: "uint256" }],
    pad(instruction.maxExecutions),
  );

  const message = {
    salt: decodedSalt,
    maxExecutions: decodedMaxExecutions,
    action: instruction.action,
    [actionType]: decodedArguments,
  };

  const signaturePayload = {
    domain,
    types,
    primaryType: "Instruction" as const,
    message,
  };

  const signature = await walletClient.signTypedData({
    account: walletClient.account,
    ...signaturePayload,
  });

  const recoveredAddress = await recoverTypedDataAddress({
    domain: signaturePayload.domain,
    types: signaturePayload.types,
    primaryType: signaturePayload.primaryType,
    message: signaturePayload.message,
    signature,
  });

  const isSignatureValid = recoveredAddress === targetAddress;
  if (!isSignatureValid) {
    throw new Error(
      "Signature verification failed. Recovered address doesn't match user address",
    );
  }

  // convert to eip-2098 format for on-chain verification
  const vrs = parseSignatureToVRS(signature);
  const activationSignature = createEIP2098Signature(vrs);

  const activationRequest: InstructionActivateRequest = {
    nickname,
    address: targetAddress,
    chainId: targetChainId,
    salt: instruction.salt,
    maxExecutions: instruction.maxExecutions,
    action: instruction.action,
    arguments: instruction.arguments,
    activationSignature,
  };

  const result = await client.instruction.activate(activationRequest);

  return result.data;
}

export interface InstructionClient {
  readonly getAll: (
    options: GetAllInstructionsOptions,
  ) => ReturnType<typeof getAll>;
  readonly details: (
    options: GetInstructionDetailsOptions,
  ) => ReturnType<typeof details>;
  readonly build: {
    readonly transfer: (
      options: TransferOptions,
    ) => ReturnType<typeof buildTransferInstruction>;
    readonly sweep: (
      options: SweepOptions,
    ) => ReturnType<typeof buildSweepInstruction>;
    readonly sweepSkipCCTPDepositAccount: (
      options: SweepSkipCCTPDepositAccountOptions,
    ) => ReturnType<typeof buildSweepSkipCCTPDepositAccountInstruction>;
    readonly swap: (
      options: SwapOptions,
    ) => ReturnType<typeof buildSwapInstruction>;
    readonly refuel: (
      options: RefuelOptions,
    ) => ReturnType<typeof buildRefuelInstruction>;
    readonly deactivate: (
      options: BuildDeactivateInstructionOptions,
    ) => ReturnType<typeof buildDeactivateInstruction>;
  };
}

export function createInstructionClient(
  client: OtimAPIClient,
  walletClient: WalletClient,
): InstructionClient {
  return {
    getAll: (options: GetAllInstructionsOptions = {}) =>
      getAll(client, walletClient, options),
    details: (options: GetInstructionDetailsOptions) =>
      details(client, walletClient, options),
    build: {
      transfer: (options: TransferOptions) =>
        buildTransferInstruction(client, walletClient, options),
      sweep: (options: SweepOptions) =>
        buildSweepInstruction(client, walletClient, options),
      sweepSkipCCTPDepositAccount: (
        options: SweepSkipCCTPDepositAccountOptions,
      ) =>
        buildSweepSkipCCTPDepositAccountInstruction(
          client,
          walletClient,
          options,
        ),
      swap: (options: SwapOptions) =>
        buildSwapInstruction(client, walletClient, options),
      refuel: (options: RefuelOptions) =>
        buildRefuelInstruction(client, walletClient, options),
      deactivate: (options: BuildDeactivateInstructionOptions) =>
        buildDeactivateInstruction(client, walletClient, options),
    },
  };
}
